"use client";

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Image from "next/image";


/**
 * Trip Coverflow Carousel Component
 * 
 * USAGE:
 * import TripCoverflowCarousel from './TripCoverflowCarousel';
 * <TripCoverflowCarousel images={yourImagesArray} autoPlayInterval={3000} />
 * 
 * NEXT.CONFIG.JS (if using next/image):
 * module.exports = {
 *   images: {
 *     domains: ['images.unsplash.com', 'images.pexels.com'],
 *   },
 * }
 * 
 * CONFIGURATION:
 * - Change autoPlayInterval prop to adjust auto-play speed (milliseconds)
 * - Modify PERSPECTIVE value below to change 3D depth
 * - Adjust transform values in getItemStyle() for different visual effects
 */

const PERSPECTIVE = 1200; // Adjust for more/less 3D depth
const DRAG_THRESHOLD = 50; // Pixels to drag before switching slide

// Sample images array - Replace with your own data or API call
const IK_FOLDER = process.env.NEXT_PUBLIC_IMAGEKIT_FOLDER;

const SAMPLE_IMAGES = [
  {
    title: "The Himalayan Backdrop",
    description: "Finding clarity amidst the serene peaks of Rishikesh.",
    imageUrl: "/Mountains.JPG",
    sourceUrl: "/Mountains.JPG"
  },
  {
    title: "Purposeful Activity",
    description: "Engaging in workshops designed to spark innovation and growth.",
    imageUrl: "/activity.JPG",
    sourceUrl: "/activity.JPG"
  },
  {
    title: "Founders' Strategy Meet",
    description: "Deep-dive sessions focusing on scaling and market domination.",
    imageUrl: "/insidemeet.JPG",
    sourceUrl: "/insidemeet.JPG"
  },
  {
    title: "Mentorship in Motion",
    description: "Learning from industry leaders who have walked the path before.",
    imageUrl: "/niharikateach.JPG",
    sourceUrl: "/niharikateach.JPG"
  },
  {
    title: "Collaborative Growth",
    description: "Building a supportive community of like-minded entrepreneurs.",
    imageUrl: "/Discuss1.JPG",
    sourceUrl: "/Discuss1.JPG"
  },
  {
    title: "Riverside Reflections",
    description: "Meaningful conversations by the banks of the holy Ganges.",
    imageUrl: "/opensitting.webp",
    sourceUrl: "/opensitting.webp"
  },
];

/**
 * Helper function to fetch images from Unsplash API (optional)
 * Requires UNSPLASH_ACCESS_KEY environment variable
 * 
 * export async function getTripImages() {
 *   const API_KEY = process.env.NEXT_PUBLIC_UNSPLASH_ACCESS_KEY;
 *   if (!API_KEY) return SAMPLE_IMAGES;
 *   
 *   try {
 *     const response = await fetch(
 *       `https://api.unsplash.com/search/photos?query=travel+destinations&per_page=10&orientation=landscape`,
 *       { headers: { Authorization: `Client-ID ${API_KEY}` } }
 *     );
 *     const data = await response.json();
 *     return data.results.map(img => ({
 *       title: img.alt_description || 'Beautiful destination',
 *       description: img.description || 'Amazing travel spot',
 *       imageUrl: img.urls.regular,
 *       sourceUrl: img.links.html
 *     }));
 *   } catch (error) {
 *     console.error('Failed to fetch images:', error);
 *     return SAMPLE_IMAGES;
 *   }
 * }
 */

export default function TripCoverflowCarousel({ 
  images = SAMPLE_IMAGES, 
  autoPlayInterval = 3000 
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [direction, setDirection] = useState(0);
  const dragStartX = useRef(0);
  const carouselRef = useRef(null);

  const totalImages = images.length;

  const handlePrev = useCallback(() => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + totalImages) % totalImages);
  }, [totalImages]);

  const handleNext = useCallback(() => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % totalImages);
  }, [totalImages]);

  // Auto-play functionality
  useEffect(() => {
    if (isPaused || autoPlayInterval <= 0) return;

    const interval = setInterval(() => {
      handleNext();
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [handleNext, isPaused, autoPlayInterval]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handlePrev]);

  const goToSlide = (index) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };

  // Calculate position relative to current index (circular)
  const getRelativePosition = (index) => {
    let diff = index - currentIndex;
    if (diff > totalImages / 2) diff -= totalImages;
    if (diff < -totalImages / 2) diff += totalImages;
    return diff;
  };

  // Get transform styles based on position
  const getItemStyle = (relativePos) => {
    // Center item
    if (relativePos === 0) {
      return {
        scale: 1,
        x: 0,
        rotateY: 0,
        opacity: 1,
        zIndex: 30,
      };
    }
    
    // Immediate neighbors
    if (Math.abs(relativePos) === 1) {
      return {
        scale: 0.75,
        x: relativePos * 280,
        rotateY: relativePos * 28,
        opacity: 0.8,
        zIndex: 20,
      };
    }
    
    // Far neighbors
    if (Math.abs(relativePos) === 2) {
      return {
        scale: 0.6,
        x: relativePos * 450,
        rotateY: relativePos * 38,
        opacity: 0.4,
        zIndex: 10,
      };
    }
    
    // Hidden items
    return {
      scale: 0.5,
      x: relativePos * 600,
      rotateY: relativePos * 45,
      opacity: 0,
      zIndex: 0,
    };
  };

  // Handle drag gestures
  const handleDragStart = (event, info) => {
    dragStartX.current = info.point.x;
    setIsPaused(true);
  };

  const handleDragEnd = (event, info) => {
    const dragDistance = info.point.x - dragStartX.current;
    
    if (Math.abs(dragDistance) > DRAG_THRESHOLD) {
      if (dragDistance > 0) {
        handlePrev();
      } else {
        handleNext();
      }
    }
    
    setTimeout(() => setIsPaused(false), 100);
  };

  return (
    // <div 
    //   className="w-full min-h-screen  py-12 px-4 sm:px-6 lg:px-8 overflow-hidden sm:mb-8 mb-2"
    //   onMouseEnter={() => setIsPaused(true)}
    //   onMouseLeave={() => setIsPaused(false)}
    //   onFocus={() => setIsPaused(true)}
    //   onBlur={() => setIsPaused(false)}
    // >
    //   <div className="max-w-7xl mx-auto">
    //     {/* Header */}
    //  <div className="text-center mb-2 sm:mb-2">
    //   <h1 className="text-3xl md:text-5xl lg:text-6xl font-black tracking-tight leading-tight">
    //     Where the Journey Met Learning
    //   </h1>
    //   <p className="text-[#FFAE00] sm:text-xl font-semibold mt-4">
    //     From sunrise hikes to late-night reflections — every moment taught us something new.
    //   </p>
    // </div>  

    //     {/* Carousel Container */}
    //     <div 
    //       ref={carouselRef}
    //     className="
    //       relative
    //       h-[300px]
    //       sm:h-[380px]
    //       md:h-[600px]
    //       mb-2 sm:mb-4
    //     "
    //     style={{ perspective: "1200px" }}
    //       role="region"
    //       aria-label="Trip highlights carousel"
    //       aria-live="polite"
    //     >
    //       <motion.div
    //         className="absolute inset-0 flex items-center justify-center cursor-grab active:cursor-grabbing"
    //         drag="x"
    //         dragConstraints={{ left: 0, right: 0 }}
    //         dragElastic={0.1}
    //         onDragStart={handleDragStart}
    //         onDragEnd={handleDragEnd}
    //       >
    //         {images.map((image, index) => {
    //           const relativePos = getRelativePosition(index);
    //           const style = getItemStyle(relativePos);
    //           const isVisible = Math.abs(relativePos) <= 2;

    //           return (
    //             <motion.div
    //               key={index}
    //               layout
    //               className="absolute w-[85%] sm:w-[70%] md:w-[60%] aspect-video will-change-transform"
    //               animate={style}
    //               transition={{
    //                 type: "spring",
    //                 stiffness: 80,
    //                 damping: 18,
    //                 mass: 0.6,
    //                 ease: "easeInOut"
    //               }}

    //               style={{
    //                 transformStyle: "preserve-3d",
    //                 pointerEvents: relativePos === 0 ? "auto" : "none"
    //               }}
    //             >
    //               {isVisible && (
    //                 <a
    //                   href={image.imageUrl}
    //                   target="_blank"
    //                   rel="noopener noreferrer"
    //                   className="block w-full h-full group"
    //                   tabIndex={relativePos === 0 ? 0 : -1}
    //                   aria-label={`${image.title}: ${image.description}. Opens in new tab.`}
    //                 >
    //                 <div
    //                     className="
    //                       relative w-full
    //                       h-[280px] sm:h-[380px] md:h-[500px] lg:h-[600px]
    //                       rounded-xl sm:rounded-2xl
    //                       overflow-hidden shadow-2xl
    //                     "
    //                   >
    //                     <Image
    //                       src={image.imageUrl}
    //                       alt={image.title}
    //                       fill
    //                       priority={index === currentIndex} 
    //                       className="object-cover transition-transform duration-500 ease-out group-hover:scale-105"
    //                       sizes="(max-width: 640px) 90vw, (max-width: 1024px) 70vw, 60vw"
    //                     />
    //                     {/* Gradient overlay */}
    //                     <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                        
    //                     {/* Caption */}
    //                     <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-8 text-white">
    //                       <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-2">
    //                         {image.title}
    //                       </h3>
    //                       <p className="text-sm sm:text-base md:text-lg text-gray-200 mb-2 sm:mb-3">
    //                         {image.description}
    //                       </p>
                         
    //                     </div>
    //                   </div>
    //                 </a>
    //               )}
    //             </motion.div>
    //           );
    //         })}
    //       </motion.div>

    //       {/* Navigation Buttons */}
    //       <button
    //         onClick={handlePrev}
    //         className="absolute left-2 sm:left-4 top-2/3 -translate-y-1/2 z-40 bg-black hover:bg-black backdrop-blur-sm text-white p-2 sm:p-3 md:p-4 rounded-full transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-black-400"
    //         aria-label="Previous slide"
    //       >
    //         <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    //           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    //         </svg>
    //       </button>

    //       <button
    //         onClick={handleNext}
    //         className="absolute right-2 sm:right-4 top-2/3 -translate-y-1/2 z-40 bg-black hover:bg-black backdrop-blur-sm text-white p-2 sm:p-3 md:p-4 rounded-full transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-black-400"
    //         aria-label="Next slide"
    //       >
    //         <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    //           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    //         </svg>
    //       </button>   
    //     </div>

    //     {/* Pagination Dots */}
    //     <div className="flex justify-center items-center gap-2 sm:gap-3 flex-wrap px-4">
    //       {images.map((_, index) => (
    //         <button
    //           key={index}
    //           onClick={() => goToSlide(index)}
    //           className={`transition-all duration-300 rounded-full focus:outline-none focus:ring-2 focus:ring-black-400 ${
    //             index === currentIndex
    //               ? 'w-8 sm:w-10 h-2 sm:h-2.5 bg-black-400'
    //               : 'w-2 sm:w-2.5 h-2 sm:h-2.5 bg-white/30 hover:bg-white/50'
    //           }`}
    //           aria-label={`Go to slide ${index + 1}`}
    //           aria-current={index === currentIndex}
    //         />
    //       ))}
    //     </div>

    //   </div>
    // </div>
    <></>
  );
}